import React from 'react';

import { alpha, Box, Chip, Paper, Typography } from '@mui/material';

import { palette, lightPalette, shape } from '@app/theme';
import { MarkdownContent } from '@components/ui/MarkdownContent';

import type { RiskListProperties } from './RiskList.types';
import type { Risk, RiskSeverity } from '@api/aiReviews/api.aiReviews.reviewTypes';



const SEVERITY_ORDER: RiskSeverity[] = ['high', 'medium', 'low'];

const SEVERITY_BORDER_COLOR: Record<RiskSeverity, string> = {
  high: palette.error.main,
  medium: '#F9A825',
  low: '#9E9E9E',
};

const SEVERITY_CHIP_COLOR: Record<RiskSeverity, string> = {
  high: palette.error.main,
  medium: '#F9A825',
  low: '#9E9E9E',
};

const SEVERITY_LABEL: Record<RiskSeverity, string> = {
  high: 'Высокий',
  medium: 'Средний',
  low: 'Низкий',
};

const CATEGORY_LABEL: Record<string, string> = {
  market: 'Рынок',
  team: 'Команда',
  legal: 'Юридический',
  technology: 'Технологический',
  financial: 'Финансы',
};

type RiskCardProperties = {
  risk: Risk;
  dataTest: string;
};

const RiskCard: React.FC<RiskCardProperties> = ({ risk, dataTest }) => {
  const borderColor = SEVERITY_BORDER_COLOR[risk.severity];
  const chipColor = SEVERITY_CHIP_COLOR[risk.severity];

  return (
    <Paper
      elevation={0}
      data-testid={dataTest}
      sx={{
        p: 2,
        borderRadius: `${shape.borderRadius}px`,
        border: `1px solid ${lightPalette.border.default}`,
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip
          label={CATEGORY_LABEL[risk.category] ?? risk.category}
          size='small'
          data-testid={`${dataTest}_category`}
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 500,
            height: 20,
            backgroundColor: alpha(lightPalette.text.muted, 0.1),
            color: 'text.secondary',
            border: 'none',
          }}
        />
        <Chip
          label={SEVERITY_LABEL[risk.severity]}
          size='small'
          data-testid={`${dataTest}_severity`}
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            height: 20,
            backgroundColor: alpha(chipColor, 0.12),
            color: chipColor,
            border: 'none',
          }}
        />
      </Box>

      <Typography
        data-testid={`${dataTest}_title`}
        sx={{
          fontSize: '0.9375rem',
          fontWeight: 700,
          color: 'text.primary',
          mb: 0.5,
          lineHeight: 1.4,
        }}
      >
        {risk.title}
      </Typography>

      <MarkdownContent variant='compact' dataTest={`${dataTest}_description`}>
        {risk.description}
      </MarkdownContent>
    </Paper>
  );
};

export const RiskList: React.FC<RiskListProperties> = ({
  risks,
  dataTest = 'risk-list',
}) => {
  const sortedRisks = [...risks].toSorted(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  );

  if (sortedRisks.length === 0) {
    return;
  }

  return (
    <Box data-testid={dataTest} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {sortedRisks.map((risk, index) => (
        <RiskCard
          key={`${risk.severity}-${risk.category}-${index}`}
          risk={risk}
          dataTest={`${dataTest}_item-${index}`}
        />
      ))}
    </Box>
  );
};
